import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const TITLE = "About Voleor: Two Brothers Building Tools for Music Makers";
const DESCRIPTION =
  "Voleor is two brothers building tools for musicians and producers: MIGHTYMAX, a producer and sound engineer who has worked with some of the biggest names in hip-hop, and Sébastien Breton, a software engineer.";

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: TITLE,
  url: "https://voleor.com/about",
  mainEntity: {
    "@type": "Organization",
    name: "Voleor",
    description: "Music software company building tools for musicians and producers.",
    url: "https://voleor.com",
    logo: "https://voleor.com/voleor-logo.png",
    sameAs: ["https://discord.gg/GxJyteBJcV"],
    founder: [
      {
        "@type": "Person",
        name: "MIGHTYMAX",
        jobTitle: "Music producer & sound engineer",
      },
      {
        "@type": "Person",
        name: "Sébastien Breton",
        jobTitle: "Software engineer",
      },
    ],
  },
};

export default function AboutUs() {
  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.head.querySelector('meta[name="description"]');
    const prevDesc = descEl?.getAttribute("content") || "";
    const canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href") || "https://voleor.com/";
    document.title = TITLE;
    descEl?.setAttribute("content", DESCRIPTION);
    canonical?.setAttribute("href", "https://voleor.com/about");
    return () => {
      document.title = prevTitle;
      descEl?.setAttribute("content", prevDesc);
      canonical?.setAttribute("href", prevCanonical);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(JSON_LD) }}
      />
      <div className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
        <Link
          to="/"
          className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] hover:text-[#FF5500]"
        >
          ← Back to TrapMan
        </Link>

        <header className="mt-8 mb-12">
          <h1 className="font-clash text-4xl font-bold sm:text-5xl">About Voleor</h1>
          <p className="mt-3 font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">
            Two brothers building tools for music makers
          </p>
        </header>

        <div className="space-y-10 font-body leading-relaxed text-[#D4D4D8]">
          <section>
            <p>
              Voleor builds tools for musicians and producers. The company is two people, a
              music producer and a software engineer who happen to be brothers, and it
              started as a decade of the same conversation: one of us complaining that music
              software keeps getting in the way at the exact moment an idea is most fragile,
              the other insisting that's just an engineering problem wearing headphones.
            </p>
            <p className="mt-4">Eventually we stopped arguing and started building.</p>
          </section>

          <Section title="MIGHTYMAX, the ears">
            <p>
              Max is a French-American producer, multi-instrumentalist and sound engineer,
              raised in Orlando, Florida and now based in Paris. He has spent his career in
              studios on both sides of the Atlantic, working on records and sessions for
              artists including Drake, DJ Khaled, Rick Ross and Eminem. In rooms like that,
              an idea that takes forty minutes to program is an idea somebody else already
              recorded.
            </p>
            <p className="mt-4">
              Every tool we ship passes through his hands and his sessions first. The rule
              is simple: if it slows the music down, it dies.
            </p>
          </Section>

          <Section title="Sébastien Breton, the hands">
            <p>
              Sébastien is a software engineer and a lifelong musician, the kind who cares
              as much about the milliseconds between a click and a sound as about the sound
              itself. He turns studio frustrations into products: grids, piano rolls,
              on-device AI, whatever the next tool needs to disappear into the workflow.
            </p>
            <p className="mt-4">
              His obsession is the space between the idea and the speaker. Every window you
              don't have to open, every keystroke that replaces six clicks: that's the
              product.
            </p>
          </Section>

          <Section title="Why we build">
            <p>
              Our tools come from the studio, not from a product roadmap, and a few
              principles apply to everything we make. We sell them{" "}
              <strong>one-time, with free lifetime updates</strong> and no subscriptions,
              because your workflow is infrastructure, and infrastructure you rent is
              leverage someone else holds over your music. They{" "}
              <strong>work offline</strong>, because studios lose Wi-Fi and sessions don't
              wait. And our{" "}
              <a href="/docs/" className="text-[#FF5500] hover:underline">
                docs
              </a>{" "}
              say honest things about the alternatives, because musicians can hear spin.
            </p>
            <p className="mt-4">
              TrapMan, our step-sequencer drum plugin, is the first tool built on those
              rules. It won't be the last.
            </p>
            <p className="mt-4">
              When you write to support, one of the two of us answers. Usually the one who
              broke it.
            </p>
          </Section>

          <Section title="Say hello">
            <p>
              The fastest way to reach us is{" "}
              <a
                href="https://discord.gg/GxJyteBJcV"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FF5500] hover:underline"
              >
                Discord
              </a>
              . And the fastest way to judge us is what we build:{" "}
              <a href="/download" className="text-[#FF5500] hover:underline">
                TrapMan's 7-day trial
              </a>{" "}
              is every feature included, no credit card.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-clash mb-3 text-2xl font-semibold text-white">{title}</h2>
      {children}
    </section>
  );
}
